import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import { storableError } from '../util/errors';
import { TRANSITION_ZERO_REQUEST_PAYMENT, transitionsToRequested } from '../util/transaction';
import { LISTING_STATE_DRAFT } from '../util/types';
import * as log from '../util/log';
import { authInfo } from './Auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { util as sdkUtil } from '../util/sdkLoader';
import { apiBaseUrl } from '../util/api';
import axios from '../util/axiosHelper';
import { preferredLang } from '../marketplace-custom-config';
import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { isEmpty } from 'lodash';

const allowedLangs = Object.values(preferredLang).map(lang => lang.value);

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CURRENT_USER_REVIEWS_REQUEST = 'app/user/CURRENT_USER_REVIEWS_REQUEST';
export const CURRENT_USER_REVIEWS_SUCCESS = 'app/user/CURRENT_USER_REVIEWS_SUCCESS';
export const CURRENT_USER_REVIEWS_ERROR = 'app/user/CURRENT_USER_REVIEWS_ERROR';

export const CURRENT_WORKER_HAS_NEW_ASSIGNED_REQUEST =
  'app/user/CURRENT_WORKER_HAS_NEW_ASSIGNED_REQUEST';
export const CURRENT_WORKER_HAS_NEW_ASSIGNED_SUCCESS =
  'app/user/CURRENT_WORKER_HAS_NEW_ASSIGNED_SUCCESS';
export const CURRENT_WORKER_HAS_NEW_ASSIGNED_ERROR =
  'app/user/CURRENT_WORKER_HAS_NEW_ASSIGNED_ERROR';

export const CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_REQUEST =
  'app/user/CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_REQUEST';
export const CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_SUCCESS =
  'app/user/CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_SUCCESS';
export const CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_ERROR =
  'app/user/CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_ERROR';

export const CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_REQUEST =
  'app/user/CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_REQUEST';
export const CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_SUCCESS =
  'app/user/CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_SUCCESS';
export const CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_ERROR =
  'app/user/CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const WORKER_EXTRA_DETAILS_REQUEST = 'app/user/WORKER_EXTRA_DETAILS_REQUEST';
export const WORKER_EXTRA_DETAILS_SUCCESS = 'app/user/WORKER_EXTRA_DETAILS_SUCCESS';
export const WORKER_EXTRA_DETAILS_ERROR = 'app/user/WORKER_EXTRA_DETAILS_ERROR';

export const USER_REVIEWS_REQUEST = 'app/user/USER_REVIEWS_REQUEST';
export const USER_REVIEWS_SUCCESS = 'app/user/USER_REVIEWS_SUCCESS';
export const USER_REVIEWS_ERROR = 'app/user/USER_REVIEWS_ERROR';

export const ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_REQUEST =
  'app/user/ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_REQUEST';
export const ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_SUCCESS =
  'app/user/ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_SUCCESS';
export const ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_ERROR =
  'app/user/ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  currentUserListing: null,
  currentUserListingFetched: false,
  workerExtraDetailsFetchInProgress: false,
  workerExtraDetailsFetchError: null,
  workerExtraDetailsAddOrUpdateInProgress: false,
  workerExtraDetailsAddOrUpdateError: null,
  currentWorkerAssignedNotification: {},
  currentWorkerAssignedNotificationError: {},
  currentBusinessAssignedNotification: {},
  currentBusinessAssignedNotificationError: {},
  currentBusinessNewAppliedNotification: {},
  currentBusinessNewAppliedNotificationError: {},
  reviews: {},
  reviewsError: null,
  userReviews: [],
  userReviewError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return { ...state, currentUser: mergeCurrentUser(state.currentUser, payload) };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CURRENT_USER_REVIEWS_REQUEST:
      return { ...state, reviewsError: null };
    case CURRENT_USER_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case CURRENT_USER_REVIEWS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, reviewsError: payload };

    case USER_REVIEWS_REQUEST:
      return {
        ...state,
        userReviews: [],
        userReviewError: null,
      };

    case USER_REVIEWS_SUCCESS:
      return {
        ...state,
        userReviews: payload,
        userReviewError: null,
      };

    case USER_REVIEWS_ERROR:
      return {
        ...state,
        userReviews: [],
        userReviewError: payload,
      };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
        currentUserListing: null,
        currentUserListingFetched: false,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return {
        ...state,
        currentUserHasListings: payload.hasListings,
        currentUserListing: payload.listing,
        currentUserListingFetched: true,
      };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };
    case WORKER_EXTRA_DETAILS_REQUEST:
      return {
        ...state,
        workerExtraDetailsFetchInProgress: true,
        workerExtraDetailsFetchError: null,
      };
    case WORKER_EXTRA_DETAILS_SUCCESS:
      return {
        ...state,
        workerExtraDetailsFetchInProgress: false,
      };
    case WORKER_EXTRA_DETAILS_ERROR:
      return {
        ...state,
        workerExtraDetailsFetchInProgress: false,
        workerExtraDetailsFetchError: payload,
      };

    case ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_REQUEST:
      return {
        ...state,
        workerExtraDetailsAddOrUpdateInProgress: true,
        workerExtraDetailsAddOrUpdateError: null,
      };

    case ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_SUCCESS:
      return {
        ...state,
        workerExtraDetailsAddOrUpdateInProgress: false,
      };

    case ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_ERROR:
      return {
        ...state,
        workerExtraDetailsAddOrUpdateInProgress: false,
        workerExtraDetailsAddOrUpdateError: payload,
      };

    case CURRENT_WORKER_HAS_NEW_ASSIGNED_REQUEST:
      return { ...state, currentWorkerAssignedNotificationError: null };
    case CURRENT_WORKER_HAS_NEW_ASSIGNED_SUCCESS:
      return { ...state, currentWorkerAssignedNotification: payload };
    case CURRENT_WORKER_HAS_NEW_ASSIGNED_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentWorkerAssignedNotificationError: payload };

    case CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_REQUEST:
      return { ...state, currentBusinessAssignedNotificationError: null };
    case CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_SUCCESS:
      return { ...state, currentBusinessAssignedNotification: payload };
    case CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentBusinessAssignedNotificationError: payload };

    case CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_REQUEST:
      return { ...state, currentBusinessNewAppliedNotificationError: null };
    case CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_SUCCESS:
      return { ...state, currentBusinessNewAppliedNotification: payload };
    case CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentBusinessNewAppliedNotificationError: payload };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const currentUserReviewsRequest = () => ({ type: CURRENT_USER_REVIEWS_REQUEST });

export const currentUserReviewsSuccess = payload => ({
  type: CURRENT_USER_REVIEWS_SUCCESS,
  payload,
});

export const currentUserReviewsError = e => ({
  type: CURRENT_USER_REVIEWS_ERROR,
  payload: e,
  error: true,
});

export const userReviewsRequest = () => ({ type: USER_REVIEWS_REQUEST });

export const userReviewsSuccess = payload => ({
  type: USER_REVIEWS_SUCCESS,
  payload,
});

export const userReviewsError = e => ({
  type: USER_REVIEWS_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = (hasListings, listing) => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings, listing },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const workerExtraDetailsRequest = () => ({
  type: WORKER_EXTRA_DETAILS_REQUEST,
});

export const workerExtraDetailsSuccess = () => ({
  type: WORKER_EXTRA_DETAILS_SUCCESS,
});

export const workerExtraDetailsError = e => ({
  type: WORKER_EXTRA_DETAILS_ERROR,
  error: true,
  payload: e,
});

export const addOrUpdateWorkerExtraDetailsRequest = () => ({
  type: ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_REQUEST,
});

export const addOrUpdateWorkerExtraDetailsSuccess = () => ({
  type: ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_SUCCESS,
});

export const addOrUpdateWorkerExtraDetailsError = e => ({
  type: ADD_OR_UPDATE_WORKER_EXTRA_DETAILS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentWorkerHasAssignedRequest = () => ({
  type: CURRENT_WORKER_HAS_NEW_ASSIGNED_REQUEST,
});

export const fetchCurrentWorkerHasAssignedSuccess = payload => ({
  type: CURRENT_WORKER_HAS_NEW_ASSIGNED_SUCCESS,
  payload,
});

const fetchCurrentWorkerHasAssignedError = e => ({
  type: CURRENT_WORKER_HAS_NEW_ASSIGNED_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentBusinessHasAcceptRequest = () => ({
  type: CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_REQUEST,
});

export const fetchCurrentBusinessHasAcceptSuccess = payload => ({
  type: CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_SUCCESS,
  payload,
});

const fetchCurrentBusinessHasAcceptError = e => ({
  type: CURRENT_BUSINESS_HAS_ACCEPT_NOTIFICATION_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentBusinessHasNewAppliedRequest = () => ({
  type: CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_REQUEST,
});

export const fetchCurrentBusinessHasNewAppliedSuccess = payload => ({
  type: CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_SUCCESS,
  payload,
});

const fetchCurrentBusinessHasNewAppliedError = e => ({
  type: CURRENT_BUSINESS_HAS_NEW_APPLIED_NOTIFICATION_ERROR,
  error: true,
  payload: e,
});
// ================ Thunks ================ //
export const fetchCurrentBusinessHasNewAssigned = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentBusinessHasAcceptRequest());
  const { currentUser } = getState().user;
  if (!currentUser) {
    dispatch(fetchCurrentBusinessHasAcceptSuccess(false));
    return Promise.resolve(null);
  }
  const url = `${apiBaseUrl()}/api/user/getWorkerNotifications`;
  const apiBody = { businessId: currentUser?.id?.uuid, businessNotify: true };
};

export const fetchBusinessHasNewApplied = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentBusinessHasNewAppliedRequest());
  const { currentUser } = getState().user;
  if (!currentUser) {
    dispatch(fetchCurrentBusinessHasNewAppliedSuccess(false));
    return Promise.resolve(null);
  }
  const isProvider = currentUser?.attributes?.profile?.publicData?.isProvider;
  if (!isProvider) {
    dispatch(fetchCurrentBusinessHasNewAppliedSuccess(false));
    return Promise.resolve(null);
  }
  const apiQueryParams = {
    only: 'sale',
    last_transitions: [TRANSITION_ZERO_REQUEST_PAYMENT],
    page: 1,
    per_page: NOTIFICATION_PAGE_SIZE,
  };

  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentBusinessHasNewAppliedSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentBusinessHasNewAppliedError(storableError(e))));
};

export const fetchCurrentWorkerHasNewAssigned = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentWorkerHasAssignedRequest());
  const { currentUser } = getState().user;
  if (!currentUser) {
    dispatch(fetchCurrentWorkerHasAssignedSuccess(false));
    return Promise.resolve(null);
  }
  const url = `${apiBaseUrl()}/api/user/getWorkerNotifications`;
  const apiBody = { workerId: currentUser?.id?.uuid, workerNotify: true };
  if (!currentUser?.attributes?.profile?.publicData?.isProvider)
    return axios
      .post(url, apiBody)
      .then(response => {
        dispatch(fetchCurrentWorkerHasAssignedSuccess(response.data));
      })
      .catch(e => dispatch(fetchCurrentWorkerHasAssignedError(storableError(e))));
};

export const fetchReviews = () => (dispatch, getState, sdk) => {
  dispatch(currentUserReviewsRequest());
  const { currentUser } = getState().user;
  if (!currentUser) {
    dispatch(currentUserReviewsSuccess(false));
    return Promise.resolve(null);
  }
  const apiBody = { workerId: currentUser?.id?.uuid, workerNotify: true };
  if (!currentUser?.attributes?.profile?.publicData?.isProvider)
    return axios
      .post(url, apiBody)
      .then(response => {
        dispatch(currentUserReviewsSuccess(response.data));
      })
      .catch(e => dispatch(currentUserReviewsError(storableError(e))));
};

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    per_page: 1,
    pub_isChatWithAdminListing: false,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;
      const listing = hasListings ? response.data.data[0] : null;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings, listing));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    per_page: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

const fetchNewCsrfToken = userId => {
  const url = `${apiBaseUrl()}/api/user/token/${userId}`;
  return axios.get(url);
};

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserNotificationsRequest());

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsToRequested,
    page: 1,
    per_page: NOTIFICATION_PAGE_SIZE,
  };

  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

export const fetchCurrentUser = (params = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().Auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }
  let apiCurrentUser;

  const parameters = params || {
    include: [
      'profileImage',
      'stripeAccount',
      'stripeCustomer',
      'stripeCustomer.defaultPaymentMethod',
    ],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return (
    sdk.currentUser
      .show(parameters)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.show response');
        }
        const currentUser = entities[0];
        // Save stripeAccount to store.stripe.stripeAccount if it exists
        if (currentUser.stripeAccount) {
          dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
        }

        // set current user id to the logger
        log.setUserId(currentUser.id.uuid);
        apiCurrentUser = currentUser;
        return fetchNewCsrfToken(currentUser.id.uuid);
      })

      .then(_ => {
        if (!apiCurrentUser.attributes.profile.publicData.isProvider) {
          return dispatch(fetchCurrentWorkerUserExtraDetails(apiCurrentUser.id.uuid));
        }
        return null;
      })
      .then(resp => {
        if (resp !== null && typeof resp === 'object') {
          Object.assign(apiCurrentUser.attributes.profile.publicData, resp);
        }
        dispatch(currentUserShowSuccess(apiCurrentUser));
        dispatch(fetchCurrentUserHasListings());
        dispatch(fetchCurrentWorkerHasNewAssigned());
        dispatch(fetchCurrentUserNotifications());
        dispatch(fetchBusinessHasNewApplied());
        if (!apiCurrentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
        // Make sure auth info is up to date
        dispatch(authInfo());
        return 'ok';
      })
      .catch(e => {
        // Make sure auth info is up to date
        dispatch(authInfo());
        log.error(e, 'fetch-current-user-failed');
        dispatch(currentUserShowError(storableError(e)));
        throw e;
      })
  );
};

export const fetchCurrentWorkerUserExtraDetails = userId => (getState, dispatch, sdk) => {
  dispatch(workerExtraDetailsRequest());
  const url = `${apiBaseUrl()}/api/user/worker/extradetails?userId=${userId}`;
  return axios
    .get(url)
    .then(resp => {
      const data = resp.data;
      dispatch(workerExtraDetailsSuccess());
      return data;
    })
    .catch(err => {
      const storableErr = storableError(err);
      dispatch(workerExtraDetailsError(storableErr));
      throw err;
    });
};

export const addOrUpdateWorkerExtraDetails = params => (dispatch, getState, sdk) => {
  dispatch(addOrUpdateWorkerExtraDetailsRequest());
  const { isUpdate, preferredLang, interviewDetails, ...restParams } = params;
  const { preferredLang: userLang } = params;
  const isValidPreferredLang = typeof userLang !== 'undefined' && allowedLangs.includes(userLang);
  const isValidInterviewDetails =
    typeof interviewDetails !== 'undefined' &&
    typeof interviewDetails.eventUri === 'string' &&
    typeof interviewDetails.inviteeUri == 'string' &&
    typeof interviewDetails.isConfirmed === 'boolean';
  const url = `${apiBaseUrl()}/api/user/worker/extradetails/${isUpdate ? 'update' : 'add'}`;
  return axios
    .post(url, { ...restParams, interviewDetails })
    .then(() => {
      const isUpdateProfileReq = isValidInterviewDetails || isValidPreferredLang;
      const data = {};
      if (isValidPreferredLang) {
        data.publicData = { preferredLang: userLang };
      }
      if (isValidInterviewDetails) {
        data.privateData = { interviewDetails };
      }
      if (isUpdateProfileReq && !isEmpty(data)) {
        return dispatch(updateProfile(data));
      }
      return dispatch(fetchCurrentUser());
    })
    .then(() => dispatch(addOrUpdateWorkerExtraDetailsSuccess()))
    .catch(err => {
      return dispatch(addOrUpdateWorkerExtraDetailsError(storableError(err)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

export const getUserReviews = data => async (dispatch, getState, sdk) => {
  dispatch(userReviewsRequest());
  axios
    .post(`${apiBaseUrl()}/api/reviews/queryReviews`, data)
    .then(res => {
      dispatch(userReviewsSuccess(res.data));
    })
    .catch(e => {
      dispatch(userReviewsError(e));
      throw e;
    });
};
